/**
 * 고가/저가 계산 유틸리티
 */
/**
 * 새로운 가격을 기반으로 고가/저가를 업데이트합니다.
 * 여러 가격(sellPrice, buyPrice 등)을 순차적으로 적용하여 누적 업데이트할 수 있습니다.
 *
 * @param options - 업데이트 옵션
 * @returns 업데이트된 고가/저가
 *
 * @example
 * ```typescript
 * // 단일 가격으로 초기화
 * let { high, low } = updateHighLow({
 *   currentHigh: undefined,
 *   currentLow: undefined,
 *   newPrice: 1.2345
 * });
 *
 * // 여러 가격으로 누적 업데이트
 * const prices = [1.2345, 1.2350, 1.2340];
 * for (const price of prices) {
 *   ({ high, low } = updateHighLow({
 *     currentHigh: high,
 *     currentLow: low,
 *     newPrice: price
 *   }));
 * }
 * ```
 */
export function updateHighLow(options) {
    const { currentHigh, currentLow, newPrice, roundFactor = 100000 } = options;
    // 새로운 가격이 없으면 기존 값 유지
    if (newPrice === undefined || newPrice === null) {
        return {
            high: currentHigh,
            low: currentLow,
        };
    }
    // 가격 반올림 (소수점 5자리까지 유지)
    const roundedPrice = Math.round(newPrice * roundFactor) / roundFactor;
    // 고가 업데이트: 현재 고가가 없거나 0이거나 새로운 가격이 더 높으면 업데이트
    let updatedHigh = currentHigh;
    if (currentHigh === undefined || currentHigh === 0 || roundedPrice > currentHigh) {
        updatedHigh = roundedPrice;
    }
    // 저가 업데이트: 현재 저가가 없거나 0이거나 새로운 가격이 더 낮으면 업데이트
    let updatedLow = currentLow;
    if (currentLow === undefined || currentLow === 0 || roundedPrice < currentLow) {
        updatedLow = roundedPrice;
    }
    return {
        high: updatedHigh,
        low: updatedLow,
    };
}
/**
 * 여러 가격을 한 번에 적용하여 고가/저가를 계산합니다.
 * sellPrice와 buyPrice를 모두 고려하여 고가/저가를 업데이트합니다.
 *
 * @param options - 여러 가격을 포함한 업데이트 옵션
 * @returns 업데이트된 고가/저가
 *
 * @example
 * ```typescript
 * const { high, low } = updateHighLowWithPrices({
 *   currentHigh: 1.2345,
 *   currentLow: 1.2340,
 *   sellPrice: 1.2348,
 *   buyPrice: 1.2342
 * });
 * ```
 */
export function updateHighLowWithPrices(options) {
    const { currentHigh, currentLow, sellPrice, buyPrice, roundFactor } = options;
    // sellPrice로 먼저 업데이트
    let result = updateHighLow({
        currentHigh,
        currentLow,
        newPrice: sellPrice,
        roundFactor,
    });
    // buyPrice로 누적 업데이트
    result = updateHighLow({
        currentHigh: result.high,
        currentLow: result.low,
        newPrice: buyPrice,
        roundFactor,
    });
    return result;
}
//# sourceMappingURL=highLow.js.map